import { computed, ref } from "vue";
import type { FilteredProductItem } from "@nwt/norce-pwa/dist/runtime/types/api/searchAndFilter/filters";
import type {
	ProductItem,
	ParametricListValue,
	ParametricListInfo
} from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import { storeToRefs } from "pinia";
import {
	findColorParametricId,
	getColorValue,
	getColorValueId,
	isColorVariant,
	getUniqueColorVariants
} from "~/utils/product/variantHelpers";

export interface ColorSwatchVariant {
	valueId: number;
	variant: FilteredProductItem;
	colorCode: string | null;
	name: string | null;
}

interface UseProductColorsOptions {
	product: Partial<FilteredProductItem> | ProductItem;
	variants?: FilteredProductItem[];
	allParametrics?: ParametricListValue[];
}

/**
 * Composable for product color swatches and variant logic.
 * - Swatches (colorSwatches) are built from the **color** parametric only: all unique colors from variants, no filter by allowed.
 */
export function useProductColors(options: UseProductColorsOptions) {
	const { product, variants, allParametrics } = options;

	const productStore = useProductStore();
	const { getParametricsInformation } = storeToRefs(productStore);

	const hoveredColorValueId = ref<number | null>(null);

	const parametricsInfo = computed<ParametricListInfo[]>(() => {
		return (getParametricsInformation.value as ParametricListInfo[]) || [];
	});

	// Color parametric ID for swatches, variants, etc. (standard "color")
	const colorParametricId = computed<number | null>(() => {
		if (!parametricsInfo.value || parametricsInfo.value.length === 0) {
			return null;
		}
		return findColorParametricId(parametricsInfo.value);
	});

	// Create color value Map once for O(1) lookups
	const colorValueMap = computed<
		Map<number, { code: string | null; name: string | null }>
	>(() => {
		const map = new Map<number, { code: string | null; name: string | null }>();

		if (
			!colorParametricId.value ||
			!allParametrics ||
			allParametrics.length === 0
		) {
			return map;
		}

		for (const param of allParametrics) {
			if (param.ParametricId === colorParametricId.value) {
				map.set(param.Id, {
					code: param.Code || null,
					name: param.Name || null
				});
			}
		}

		return map;
	});

	// Filter color variants once
	const colorVariantsList = computed<FilteredProductItem[]>(() => {
		if (!colorParametricId.value || !variants || variants.length === 0) {
			return [];
		}

		return variants.filter((v) => isColorVariant(v, colorParametricId.value));
	});

	const colorVariants = computed(() => {
		if (
			!colorParametricId.value ||
			!allParametrics ||
			allParametrics.length === 0 ||
			colorVariantsList.value.length === 0
		) {
			return [];
		}

		// Don't filter again on frontend - show all variants that came from backend
		const uniqueColors = getUniqueColorVariants(
			colorVariantsList.value,
			colorParametricId.value,
			undefined,
			undefined,
			allParametrics
		);

		if (uniqueColors.length <= 1) {
			return [];
		}

		return uniqueColors;
	});

	const colorSwatches = computed<ColorSwatchVariant[]>(() => {
		if (
			!colorParametricId.value ||
			!allParametrics ||
			allParametrics.length === 0 ||
			colorVariantsList.value.length === 0
		) {
			return [];
		}

		const swatchesByColorValueId = new Map<number, ColorSwatchVariant>();

		// Use color parametric only: show all unique colors from variants (no filter by allowed)
		colorVariantsList.value.forEach((variant) => {
			const colorValueId = getColorValueId(variant, colorParametricId.value);
			if (colorValueId === null) {
				return;
			}

			if (swatchesByColorValueId.has(colorValueId)) {
				return;
			}

			const valueData = colorValueMap.value.get(colorValueId);
			const colorCode =
				valueData?.code ||
				valueData?.name ||
				getColorValue(variant, colorParametricId.value, allParametrics);

			swatchesByColorValueId.set(colorValueId, {
				valueId: colorValueId,
				variant,
				colorCode: colorCode || null,
				name: valueData?.name || valueData?.code || null
			});
		});

		const uniqueSwatches = Array.from(swatchesByColorValueId.values());

		if (uniqueSwatches.length === 0) {
			return [];
		}

		return uniqueSwatches;
	});

	const currentColorValueId = computed<number | null>(() => {
		if (!colorParametricId.value || !product) {
			return null;
		}

		return getColorValueId(
			product as FilteredProductItem,
			colorParametricId.value
		);
	});

	return {
		hoveredColorValueId,
		colorParametricId,
		colorVariants,
		colorSwatches,
		currentColorValueId
	};
}
